import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { Toaster } from "sonner";
import { useEffect } from "react";

import appCss from "../styles.css?url";
import { captureUtmParams } from "@/lib/utmParams";
import { SiteHeader } from "@/components/marketing/SiteHeader";
import { SiteFooter } from "@/components/marketing/SiteFooter";


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "3wabSOMlm73BvKnbVzJslaurrdLFuQcHSmYZg479jFk" },
      { name: "google-site-verification", content: "3WX0NJajnJiDp59pdCmz-GeYr7GkPcSb89_YNnlDS6c" },
      { name: "author", content: "KnowMeno" },
      { name: "publisher", content: "KnowMeno" },
      { name: "application-name", content: "KnowMeno" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "KnowMeno" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@knowmeno" },
      { name: "theme-color", content: "#4B1F4F" },
      { name: "format-detection", content: "telephone=no" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "apple-mobile-web-app-title", content: "KnowMeno" },
      // og:image / twitter:image intentionally NOT set at root — each leaf
      // route owns its own share image so social previews stay page-specific.
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://knowmeno.com/#organization",
              name: "KnowMeno",
              url: "https://knowmeno.com",
              logo: {
                "@type": "ImageObject",
                url: "https://knowmeno.com/icon-512.png",
                width: 512,
                height: 512,
              },
              description:
                "KnowMeno helps women track menopause symptoms and prepare for clearer doctor conversations.",
              sameAs: [
                "https://www.facebook.com/knowmeno",
                "https://www.instagram.com/knowmeno",
              ],
            },
            {
              "@type": "WebSite",
              "@id": "https://knowmeno.com/#website",
              name: "KnowMeno",
              url: "https://knowmeno.com",
              inLanguage: ["en", "es"],
              description:
                "Daily symptom tracking and a doctor-ready Symptom Intelligence Report for perimenopause and menopause.",
              publisher: { "@id": "https://knowmeno.com/#organization" },
            },

            {
              "@type": "MobileApplication",
              name: "KnowMeno",
              operatingSystem: "iOS, Web",
              applicationCategory: "HealthApplication",
              url: "https://knowmeno.com",
              description:
                "A wellness companion that helps track menopause symptoms and supports awareness for doctor conversations. Not a medical device; does not diagnose, treat, or cure any condition.",
              offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
              publisher: { "@id": "https://knowmeno.com/#organization" },
            },
          ],
        }),
      },
      {
        src: "https://app.termly.io/resource-blocker/d8d7f41f-38a8-4ca0-adb4-97ed1aae4aa9?autoBlock=on",
        defer: true,
      },
    ],

    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Gloock&family=Instrument+Serif:ital@0;1&family=Lora:ital,wght@0,400;0,500;0,600;1,400&family=Work+Sans:wght@300;400;500;600;700&display=swap" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico?v=2" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png?v=2" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png?v=2" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png?v=2" },
      { rel: "manifest", href: "/manifest.json" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Toaster position="top-center" richColors closeButton />
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  useEffect(() => {
    captureUtmParams();
  }, []);
  return (
    <>
      <SiteHeader />
      <main>
        <Outlet />
      </main>
      <SiteFooter />
    </>
  );
}
